import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { ThreeDots } from 'react-loader-spinner';
import '../AdminOrderModal/css/AdminOrderModal.css';
import './css/AdminUserModal.css';
import { getUserOrders, deleteUser } from '../../../Utils/RestUtils';
import {
  SUCCESS_STATUS,
  ERROR_TEXT,
} from '../../../Utils/Constants/GlobalConstants';
import {
  YES,
  NO,
  EMPTY_VALUE,
  USER_EMAIL,
  USER_REGISTRATION_DATE,
  USER_EMAIL_VERIFIED,
  USER_HAS_PASSWORD,
  USER_ROLES,
  USER_ROLE_TITLES,
  USER_ID,
  USER_DETAILS_TITLE,
  USER_ORDERS_TITLE,
  NO_USER_ORDERS,
  CREATE_ORDER,
  DELETE_USER,
  DELETE_USER_CONFIRM,
  DELETE_USER_HINT,
  DELETE_USER_ORDERS,
  DELETE_USER_SELF,
  ARCHIVED_BADGE,
} from '../../../Utils/Constants/AdminConstants';
import OrderStatusBadge from '../OrderStatusBadge/OrderStatusBadge';
import OrderStageBadge from '../OrderStageBadge/OrderStageBadge';
import AdminCreateOrderModal from '../AdminCreateOrderModal/AdminCreateOrderModal';
import AdminOrderModal from '../AdminOrderModal/AdminOrderModal';
import { ReactComponent as Close } from '../../../images/close-r.svg';

function AdminUserModal(props) {
  const {
    isVisible,
    modalNode,
    handleModalClick,
    user,
    handleOrdersChange,
    handleUserDelete,
  } = props;
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [ordersReloadKey, setOrdersReloadKey] = useState(0);
  const [isCreateVisible, setIsCreateVisible] = useState(false);
  const createModalNode = useRef(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isOrderVisible, setIsOrderVisible] = useState(false);
  const orderModalNode = useRef(null);
  const [isDeleteConfirm, setIsDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState('');
  const userId = user ? user.id : null;

  // ask for delete confirmation again for every opened user
  useEffect(() => {
    setIsDeleteConfirm(false);
    setDeleteMessage('');
  }, [isVisible, userId]);

  const removeUser = () => {
    const token = window.localStorage.getItem('accessToken');

    setIsDeleting(true);
    setDeleteMessage('');

    deleteUser(userId, token)
      .then((response) => {
        const result = response ? response.data : null;

        if (result === SUCCESS_STATUS) {
          handleUserDelete(userId);
        } else {
          setDeleteMessage(result === 'SELF' ? DELETE_USER_SELF : ERROR_TEXT);
        }
      })
      .catch(() => {
        setDeleteMessage(ERROR_TEXT);
      })
      .finally(() => {
        setIsDeleting(false);
        setIsDeleteConfirm(false);
      });
  };

  const handleDeleteClick = () => {
    if (isDeleteConfirm) {
      removeUser();
    } else {
      setIsDeleteConfirm(true);
    }
  };

  const closeCreateModal = useCallback(() => {
    setIsCreateVisible(false);
  }, []);

  const closeOrderModal = useCallback(() => {
    setIsOrderVisible(false);
  }, []);

  // nested modals never outlive the user modal
  useEffect(() => {
    if (!isVisible) {
      setIsCreateVisible(false);
      setIsOrderVisible(false);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isOrderVisible) {
      return undefined;
    }

    const handleOutsideClick = (e) => {
      const node = orderModalNode.current;

      if (
        !node ||
        !e.target.isConnected ||
        node.contains(e.target) ||
        e.target.closest('.aum_order_link')
      ) {
        return;
      }

      closeOrderModal();
    };

    document.addEventListener('click', handleOutsideClick, false);

    return () => {
      document.removeEventListener('click', handleOutsideClick, false);
    };
  }, [isOrderVisible, closeOrderModal]);

  const openOrderModal = (orderId) => {
    setSelectedOrderId(orderId);
    setIsOrderVisible(true);
  };

  const notifyOrdersChange = () => {
    if (handleOrdersChange) {
      handleOrdersChange();
    }
  };

  const updateOrder = (orderId, changes) => {
    setOrders((prevOrders) =>
      prevOrders.map((item) =>
        (item.id === orderId ? { ...item, ...changes } : item)));
    notifyOrdersChange();
  };

  const removeOrder = (orderId) => {
    closeOrderModal();
    setOrders((prevOrders) => prevOrders.filter((item) => item.id !== orderId));
    notifyOrdersChange();
  };

  // body scroll is already locked by the user modal
  useEffect(() => {
    if (!isCreateVisible) {
      return undefined;
    }

    const handleOutsideClick = (e) => {
      const node = createModalNode.current;

      if (
        !node ||
        !e.target.isConnected ||
        node.contains(e.target) ||
        e.target.closest('.aum_create_order')
      ) {
        return;
      }

      closeCreateModal();
    };

    document.addEventListener('click', handleOutsideClick, false);

    return () => {
      document.removeEventListener('click', handleOutsideClick, false);
    };
  }, [isCreateVisible, closeCreateModal]);

  const handleUserOrderCreated = () => {
    closeCreateModal();
    setOrdersReloadKey((key) => key + 1);
    notifyOrdersChange();
  };

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    // Esc closes only the nested modal when it is open
    const handleEscPress = (e) => {
      if (e.key === 'Escape' && !isCreateVisible && !isOrderVisible) {
        handleModalClick();
      }
    };

    document.addEventListener('keydown', handleEscPress, false);

    return () => {
      document.removeEventListener('keydown', handleEscPress, false);
    };
  }, [isVisible, isCreateVisible, isOrderVisible, handleModalClick]);

  useEffect(() => {
    if (!isVisible || !userId) {
      return undefined;
    }

    // drop the response if another user was opened meanwhile
    let isActual = true;
    const token = window.localStorage.getItem('accessToken');

    setOrders([]);
    setIsError(false);
    setIsLoading(true);

    getUserOrders(userId, token)
      .then((response) => {
        if (isActual && response && Array.isArray(response.data)) {
          setOrders(response.data);
        } else if (isActual) {
          setIsError(true);
        }
      })
      .catch(() => {
        if (isActual) {
          setIsError(true);
        }
      })
      .finally(() => {
        if (isActual) {
          setIsLoading(false);
        }
      });

    return () => {
      isActual = false;
    };
  }, [isVisible, userId, ordersReloadKey]);

  const getDateText = (date) => {
    if (!date) {
      return EMPTY_VALUE;
    }

    return moment(date).format('DD.MM.YYYY HH:mm');
  };

  const getRolesText = (roles) => {
    if (!roles || roles.length === 0) {
      return EMPTY_VALUE;
    }

    return roles.map((role) => USER_ROLE_TITLES[role] || role).join(', ');
  };

  const renderRow = (label, value) => (
    <div key={`aum-row-${label}`} className="aom_row">
      <div className="aom_label gr_text_14">{label}</div>
      <div className="aom_value br_text_14">{value}</div>
    </div>
  );

  const renderOrder = (order) => (
    <button
      key={`aum-order-${order.id}`}
      type="button"
      className="aum_order aum_order_link"
      onClick={() => openOrderModal(order.id)}
    >
      <div className="aum_order_head">
        <div className="dbm_text_16">{`№${order.externalId}`}</div>
        <div className="bm_text_16">
          {`${order.originalPrice} ${order.currency}`}
        </div>
      </div>
      <div className="bm_text_14 mb_5">
        {`${order.hotelName} ${order.hotelCategory}*`}
      </div>
      <div className="gr_text_14 mb_5">
        {`${order.countryTo}, ${order.resort} · ${order.departureDate}`}
      </div>
      <div className="aum_order_badges">
        {order.archived ? (
          <span className="aom_archived bm_text_14">{ARCHIVED_BADGE}</span>
        ) : (
          <>
            <OrderStatusBadge status={order.status} />
            <OrderStageBadge order={order} />
          </>
        )}
      </div>
    </button>
  );

  const renderOrders = () => {
    if (isLoading) {
      return (
        <ThreeDots
          visible
          height="30"
          width="30"
          color="rgba(0, 0, 0, .3)"
          radius="5"
          ariaLabel="three-dots-loading"
        />
      );
    }

    if (isError) {
      return <div className="gr_text_14">{ERROR_TEXT}</div>;
    }

    if (orders.length === 0) {
      return <div className="gr_text_14">{NO_USER_ORDERS}</div>;
    }

    return <div className="aum_orders">{orders.map(renderOrder)}</div>;
  };

  const renderBody = () => (
    <div className="aom_body custom_scroll_container">
      <div className="aom_section">
        <div className="bm_text_18 mb_12">{USER_DETAILS_TITLE}</div>
        <div className="aom_rows">
          {renderRow(USER_EMAIL, user.email || EMPTY_VALUE)}
          {renderRow(USER_REGISTRATION_DATE, getDateText(user.registrationDate))}
          {renderRow(USER_EMAIL_VERIFIED, user.emailVerified ? YES : NO)}
          {renderRow(USER_HAS_PASSWORD, user.hasPassword ? YES : NO)}
          {renderRow(USER_ROLES, getRolesText(user.roles))}
          {renderRow(USER_ID, user.id)}
        </div>
      </div>
      <div className="aom_section">
        <div className="aum_orders_title mb_12">
          <div className="bm_text_18">
            {isLoading || isError
              ? USER_ORDERS_TITLE
              : `${USER_ORDERS_TITLE} (${orders.length})`}
          </div>
          <button
            type="button"
            className="ap_small_btn btn__yellow bm_text_14 aum_create_order"
            onClick={() => setIsCreateVisible(true)}
          >
            {CREATE_ORDER}
          </button>
        </div>
        {renderOrders()}
      </div>
    </div>
  );

  const renderActions = () => (
    <div className="aom_actions">
      <div className="aum_delete_line">
        <div className="gr_text_14">
          {DELETE_USER_HINT}
          {isDeleteConfirm && !isLoading && (
            <div className="rr_text_14">
              {`${DELETE_USER_ORDERS}: ${orders.length}`}
            </div>
          )}
          {deleteMessage.length > 0 && (
            <div className="bm_text_14 aom_message aom_message_error">
              {deleteMessage}
            </div>
          )}
        </div>
        <button
          type="button"
          className={`aom_btn bm_text_16 ${
            isDeleteConfirm ? 'aom_btn_delete_confirm' : 'light_gray_button'
          }`}
          disabled={isDeleting}
          onClick={handleDeleteClick}
        >
          {isDeleteConfirm ? DELETE_USER_CONFIRM : DELETE_USER}
        </button>
      </div>
    </div>
  );

  return (
    <div className={`aom_container ${isVisible ? 'aom_open' : ''}`}>
      <div
        className="aom_modal"
        ref={(node) => {
          modalNode.current = node;
        }}
      >
        {user && (
          <div className="aom_title_line">
            <div className="aom_title aum_title">{user.email}</div>
            <button
              type="button"
              className="aom_close_but"
              onClick={handleModalClick}
            >
              <Close />
            </button>
          </div>
        )}

        {/* remount on open to reset scroll */}
        {isVisible && user && renderBody()}

        {user && renderActions()}
      </div>
      <AdminCreateOrderModal
        isVisible={isVisible && isCreateVisible}
        modalNode={createModalNode}
        handleModalClick={closeCreateModal}
        handleOrderCreated={handleUserOrderCreated}
        defaultEmail={user ? user.email || '' : ''}
      />
      <AdminOrderModal
        isVisible={isVisible && isOrderVisible}
        modalNode={orderModalNode}
        handleModalClick={closeOrderModal}
        order={orders.find((order) => order.id === selectedOrderId) || null}
        handleOrderUpdate={updateOrder}
        handleOrderDelete={removeOrder}
      />
    </div>
  );
}

AdminUserModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  modalNode: PropTypes.instanceOf(Object).isRequired,
  handleModalClick: PropTypes.func.isRequired,
  user: PropTypes.instanceOf(Object),
  handleOrdersChange: PropTypes.func,
  handleUserDelete: PropTypes.func.isRequired,
};

AdminUserModal.defaultProps = {
  user: null,
  handleOrdersChange: null,
};

export default AdminUserModal;
